import React, {useEffect, useState} from 'react';
import styles from "./emailParsing.module.scss";
import {Button, Form, Input, message as messageAnt, Space} from 'antd';
import {useDispatch} from "react-redux";
import {useEmailParsing} from "../../hooks/emailParsing.hook";
import {v4 as uuidv4} from "uuid";
import {countryArr, excludeList} from "../../shared/emailParsingConfig";
import {StatusEmailParserTypes} from "../../shared/types";

const {TextArea} = Input;

interface ILink {
    url: string
    email: string
}

const EmailParsing = () => {

    const dispatch = useDispatch()

    const {loading, sendUrlList, linkParseFromHtml, getStatus, stopParser, getEmailListFromParser, error, clearError} = useEmailParsing()

    const [searchQueryValue, setSearchQueryValue] = useState<string>('')

    const [linksCopied, setLinksCopied] = useState<boolean[]>([])
    const [googleLinksValue, setGoogleLinksValue] = useState<string[]>([])
    const [htmlValue, setHtmlValue] = useState<string>('')
    const [links, setLinks] = useState<ILink[]>([])
    const [status, setStatus] = useState<StatusEmailParserTypes | undefined>(undefined)

    const changeSearchQueryHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQueryValue(e.target.value)
    }

    const btnCopyHandler = async (index: number) => {
        await navigator.clipboard.writeText(googleLinksValue[index])
        const newLinksCopied = [...linksCopied]
        newLinksCopied[index] = true
        setLinksCopied([...newLinksCopied])
    }

    //Получение поисковых ссылок Google для поиска по ключу и разным странам
    const getSearchLinks = async () => {
        if (searchQueryValue) {
            //заменим пробелы на + в поисковом запросе для гугла
            const searchQuery = searchQueryValue.split(' ').join('+')
            //генерируем ссылки для поиска
            const googleLinks: string[] = []
            countryArr.forEach((country) => {
                const searchGoogleLink = `view-source:https://www.google.com/search?hl=ru&as_q=${searchQuery}&as_epq=&as_oq=&as_eq=&as_nlo=&as_nhi=&lr=&cr=${country.country}&as_qdr=all&as_sitesearch=&as_occt=any&as_filetype=&tbs=&num=${country.num}`
                googleLinks.push(searchGoogleLink)
            })
            setGoogleLinksValue(googleLinks)
        }
    };

    const changeHtmlHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setHtmlValue(e.target.value)
    }

    //Парсинг ссылок на сайты с html кода страницы поиска Google
    const htmlParse = async () => {
        try {
            const newLinks = linkParseFromHtml(htmlValue)

            if (newLinks.length !== 0) {
                const linkArr: ILink[] = []
                newLinks?.forEach((link) => {
                    linkArr.push({url: link, email: '...'})
                })

                setLinks([...links, ...linkArr])
                setHtmlValue('')
            }
        } catch (e) {
            messageAnt.info(error);
        }
    };

    //Отправка списка url адресов сайтов на сервер для парсинга из этих сайтов почтовых адресов и запуск парсинга или остановка если он уже запущен
    const startStopParsing = async () => {
        //запуск парсера
        if (status === StatusEmailParserTypes.disable && links.length !== 0) {
            try {
                const data = await sendUrlList(links)
                messageAnt.info(data.message)
                setStatus(StatusEmailParserTypes.enable)
            } catch (e) {
                messageAnt.info(error);
                clearError()
            }
        } else if (status === StatusEmailParserTypes.enable || status === StatusEmailParserTypes.ended) {//остановка или сброс данных в парсере после завершения парсинга
            try {
                const data = await stopParser()
                setLinks([])
                messageAnt.info(data.message)
                setStatus(StatusEmailParserTypes.disable)
            } catch (e) {
                messageAnt.info(error);
                clearError()
            }
        }
    };

    //Функция получения статуса работы парсера
    const getStatusOfParser = async () => {
        try {
            const data = await getStatus()
            messageAnt.info(data.message)
            return data.info
        } catch (e) {
            messageAnt.info(error);
            clearError()
            return undefined
        }
    };

    //изначальное получение статуса работы парсера
    useEffect(() => {
        const func = async () => {
            const newStatus = await getStatusOfParser()
            setStatus(newStatus)
        }
        func()
    }, [])

    //Функция получения списка полученных email адресов
    const getEmailList = async () => {
        try {
            const data = await getEmailListFromParser()
            messageAnt.info(data.message)
            return data.info
        } catch (e) {
            messageAnt.info(error);
            clearError()
            return undefined
        }
    };

    //Получить статус парсинга в данный момент и если запущен, то получить уже полученные данные
    const updateStatusOfParsing = async () => {
        const newStatus = await getStatusOfParser()
        setStatus(newStatus)
        if (newStatus === StatusEmailParserTypes.enable) {
            messageAnt.info('Парсер работает');
        } else if (newStatus === StatusEmailParserTypes.disable) {
            messageAnt.info('Парсер остановлен');
        } else if (newStatus === StatusEmailParserTypes.ended) {
            messageAnt.info('Парсер завершил работу');
        }
        //если сбор почтовых ящиков звершен, то получаем и сохраняем данные
        if (newStatus === StatusEmailParserTypes.ended) {
            const emailList = await getEmailList()
            if (emailList) {
                //сохраняем массив полученных адресов
                setLinks(emailList)
            }
        }
    }

    //Очистить от дублей и не нужных сайтов
    const clearDuplicatesHandler = async () => {
        const linksWithoutDouble: ILink[] = []
        links.forEach((link) => {
            let wasAdd = false
            let isInExclude = false
            linksWithoutDouble.forEach((elem) => {
                if (elem.url === link.url) {
                    wasAdd = true
                }
            })
            if (!wasAdd) {
                excludeList.forEach((elem) => {
                    if (link.url.includes(elem)) {
                        isInExclude = true
                    }
                })
                if (!wasAdd && !isInExclude) {
                    linksWithoutDouble.push(link)
                }
            }
        })
        setLinks(linksWithoutDouble)
    }

    //Скопировать все email в нужном формате
    const copyResultHandler = async () => {
        let strToCopy = ''
        links.forEach((link) => {
            strToCopy += `${link.url}\t${link.email}\n`
        })
        await navigator.clipboard.writeText(strToCopy)
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <p className={styles.title}>Парсинг почтовых адресов для рассылки</p>
                </div>
                <div className={styles.table}>
                    <h5>Парсинг почтовых адресов</h5>
                    <div className={[styles.settings, styles.blockWrap].join(' ')}>
                        <p className={styles.title}>Исходные данные</p>
                        <div className={styles.form}>
                            <p className={styles.label}>Поисковый запрос Google</p>
                            <Input value={searchQueryValue} onChange={changeSearchQueryHandler}/>
                            <div className={styles.buttons}>
                                <Button type="primary" style={{marginTop: 15}} disabled={!searchQueryValue}
                                        onClick={getSearchLinks}>
                                    Получить поисковые ссылки Google
                                </Button>
                            </div>
                            <p className={styles.label}>Поисковые ссылки Google</p>
                            <div className={styles.googleLinks}>
                                {
                                    googleLinksValue.map((link, index) => (
                                        <div className={[styles.link, linksCopied[index] && styles.copied].join(' ')}
                                             key={uuidv4()}>
                                            <Button size={'small'} type={linksCopied[index] ? 'primary' : 'default'}
                                                    onClick={() => btnCopyHandler(index)}>
                                                copy
                                            </Button>
                                            <p>{link}</p>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                        <div className={styles.form}>
                            <p className={styles.label}>Html поиска Google по скопированной ссылке</p>
                            <TextArea rows={4} value={htmlValue} onChange={changeHtmlHandler}/>
                            <div className={styles.buttons}>
                                <Button type="primary" style={{marginTop: 15}} disabled={!htmlValue || loading}
                                        onClick={htmlParse}>
                                    Добавить ссылки на сайты
                                </Button>
                            </div>
                        </div>
                        <div className={styles.form}>
                            <p className={styles.label}>Обработка данных</p>
                            <div className={styles.buttons}>
                                <Button type="primary" disabled={links.length === 0}
                                        onClick={clearDuplicatesHandler}>
                                    Очистить дубли
                                </Button>
                                <Button type="primary" onClick={startStopParsing}>
                                    {
                                        status === StatusEmailParserTypes.disable && 'Запустить парсинг'
                                    }
                                    {
                                        status === StatusEmailParserTypes.enable && 'Остановить парсинг'
                                    }
                                    {
                                        status === StatusEmailParserTypes.ended && 'Парсинг завершен'
                                    }
                                </Button>
                                <Button type="primary"
                                        onClick={updateStatusOfParsing}>
                                    Проверить статус
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className={[styles.results, styles.blockWrap].join(' ')}>
                        <div className={styles.result}>
                            <p className={styles.title}>Результат</p>
                            <Button size={'small'} onClick={copyResultHandler} disabled={links.length === 0}
                            >
                                скопировать
                            </Button>
                        </div>
                        <div className={styles.listLink}>
                            {
                                links.map((link) => (
                                    <div className={styles.linkWrap} key={uuidv4()}>
                                        <p className={styles.linkUrl}>{link.url}</p>
                                        <p className={styles.linkEmail}>{link.email}</p>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailParsing;